// src/features/task/useTaskSearch.ts
// Debounced task search for autocomplete pickers. Mirrors the search pattern
// already written for the dependency-target picker in TaskDependencies.tsx so
// every "find a task" control in this feature behaves the same way.

import { useEffect, useState } from 'react';
import { taskApi } from '@core/api';
import type { Task } from '@core/types/task';
import { getEntityId } from '@/features/task/logic';

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 10;

export function useTaskSearch(query: string, excludeId?: string) {
  const [results, setResults] = useState<Task[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const handle = setTimeout(() => {
      taskApi.ListTasks({
        pagination: { limit: SEARCH_LIMIT, cursor: '', direction: 'forward' },
        sort: [],
        filters: { search_query: query.trim() } as any,
      } as any)
        .then((res) => {
          if (cancelled) return;
          const tasks: Task[] = res?.tasks?.tasks || [];
          setResults(excludeId ? tasks.filter((tk) => getEntityId(tk) !== excludeId) : tasks);
        })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, SEARCH_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [query, excludeId]);

  return { results, searching };
}
